import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import type { MenuProps } from '@mui/material/Menu';
import type { PaperProps } from '@mui/material/Paper';
import { PopoverPaper } from '@mui/material/Popover';
import type { Theme } from '@mui/material/styles';
import type { SxProps } from '@mui/material/styles';
import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

import { getStandardDrawerFloatingSx, useStandardDrawerLayerContainer } from 'components/Drawers/StandardDrawer';

import { QMenuCloseProvider } from '../shared/QMenuCloseContext';
import { QMenuScrollArrow } from '../shared/QMenuScrollArrow';
import { QMenuSubLevelProvider } from '../shared/QMenuSubLevelContext';
import type { QMenuProps } from '../types';

type QMenuPaperLayout = {
  width: number | string;
  maxHeight: number | string;
};

type QMenuPaperSlotContextValue = {
  /** Scroll container the up/down scroll-arrow overlays track. */
  listEl: HTMLElement | null;
  /** Paper layout for the scroll-arrows / footer slot (inline styles — slot sx was unreliable). */
  layout: QMenuPaperLayout;
  header: React.ReactNode;
  footer: React.ReactNode;
  /** Ref to the sticky header chrome container (for Shift+Tab from list → search). */
  headerElRef: React.RefObject<HTMLDivElement | null> | null;
  /** Ref to the sticky footer chrome container (for Tab → Done focus). */
  footerElRef: React.RefObject<HTMLDivElement | null> | null;
  /** Last focused list row when Tab leaves the list for header/footer chrome. */
  lastListFocusRef: React.RefObject<HTMLElement | null> | null;
  scrollArrows: boolean;
};

/**
 * Single context for everything {@link QMenuScrollPaperSlot} needs. All of these values
 * are only ever read together by that one slot component, so they're bundled instead of
 * split into one context per field.
 */
const QMenuPaperSlotContext = createContext<QMenuPaperSlotContextValue>({
  listEl: null,
  layout: { width: 180, maxHeight: 'calc(100% - 96px)' },
  header: null,
  footer: null,
  headerElRef: null,
  footerElRef: null,
  lastListFocusRef: null,
  scrollArrows: false,
});

const CHROME_FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const LIST_FOCUSABLE_SELECTOR = '[role="menuitem"], [role="menuitemcheckbox"], [role="menuitemradio"]';
/** Same roles as {@link LIST_FOCUSABLE_SELECTOR}, but only the roving-tabindex-active row.
 * `[tabindex="0"]` must be appended to every comma-branch individually — appending it to
 * the joined selector string only binds to the last branch. */
const LIST_ACTIVE_ITEM_SELECTOR = LIST_FOCUSABLE_SELECTOR.split(', ')
  .map((selector) => `${selector}[tabindex="0"]`)
  .join(', ');

function toCssSize(value: number | string): string {
  return typeof value === 'number' ? `${value}px` : value;
}

function getChromeFocusables(chrome: HTMLElement): HTMLElement[] {
  return Array.from(chrome.querySelectorAll<HTMLElement>(CHROME_FOCUSABLE_SELECTOR)).filter(
    (el) => !el.hasAttribute('disabled') && el.getAttribute('aria-disabled') !== 'true',
  );
}

function isElementOrContained(container: HTMLElement | undefined, target: Element | null): boolean {
  return Boolean(container && target && (container === target || container.contains(target)));
}

/** `header`/`footer` are ReactNode — 0 and '' are renderable content, only null/undefined/false are not. */
function isRenderableNode(node: React.ReactNode): boolean {
  return node !== null && node !== undefined && node !== false;
}

/**
 * PopoverPaper with optional scroll-arrow + sticky header/footer chrome for QSelect.
 * Must keep PopoverPaper (position: absolute) or Menu anchoring breaks.
 * Arrows are absolute overlays within the list region (siblings of MenuList, not
 * list children) so list content scrolls under a paper→transparent fade.
 * Header sits above the list region; footer sits below — both flex-shrink:0 paper chrome.
 */
function QMenuScrollPaperSlot({ children, sx, style, ref, ...other }: PaperProps) {
  const { listEl, layout, header, footer, headerElRef, footerElRef, lastListFocusRef, scrollArrows } =
    useContext(QMenuPaperSlotContext);
  const cssWidth = toCssSize(layout.width);
  const cssMaxHeight = toCssSize(layout.maxHeight);

  return (
    <PopoverPaper
      ref={ref}
      {...other}
      style={{
        ...style,
        width: cssWidth,
        minWidth: cssWidth,
        maxHeight: cssMaxHeight,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
      sx={[
        {
          py: 0,
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {isRenderableNode(header) ? (
        <Box
          ref={headerElRef}
          onKeyDown={(event) => {
            // List rows use roving tabindex (-1), so Tab from the last header
            // control (e.g. search) would skip the list. Send focus to the list
            // instead (mirror of Shift+Tab → header in onClose).
            if (event.key !== 'Tab' || event.shiftKey || !listEl || !headerElRef?.current) {
              return;
            }
            const focusables = getChromeFocusables(headerElRef.current);
            const last = focusables[focusables.length - 1];
            if (!isElementOrContained(last, event.target as Element | null)) {
              return;
            }
            event.preventDefault();
            event.stopPropagation();
            const remembered = lastListFocusRef?.current;
            if (remembered && listEl.contains(remembered)) {
              remembered.focus();
              return;
            }
            const activeListItem = listEl.querySelector<HTMLElement>(LIST_ACTIVE_ITEM_SELECTOR);
            const listItems = listEl.querySelectorAll<HTMLElement>(LIST_FOCUSABLE_SELECTOR);
            const fallback = listItems[0];
            (activeListItem ?? fallback)?.focus();
          }}
          sx={{
            flex: '0 0 auto',
            px: 1,
            pt: 1,
            pb: 0,
          }}
        >
          {header}
        </Box>
      ) : null}
      <Box
        sx={{
          position: 'relative',
          flex: '1 1 0%',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {scrollArrows ? <QMenuScrollArrow direction='up' scrollParent={listEl} /> : null}
        {children}
        {scrollArrows ? <QMenuScrollArrow direction='down' scrollParent={listEl} /> : null}
      </Box>
      {isRenderableNode(footer) ? (
        <Box
          ref={footerElRef}
          onKeyDown={(event) => {
            // List rows use roving tabindex (-1), so once focus is in the footer they
            // are skipped by native Tab order. Shift+Tab from the first footer control
            // must return focus to the list (mirror of Tab → footer in onClose).
            if (event.key !== 'Tab' || !event.shiftKey || !listEl || !footerElRef?.current) {
              return;
            }
            const focusables = getChromeFocusables(footerElRef.current);
            const first = focusables[0];
            if (!isElementOrContained(first, event.target as Element | null)) {
              return;
            }
            event.preventDefault();
            event.stopPropagation();
            const remembered = lastListFocusRef?.current;
            if (remembered && listEl.contains(remembered)) {
              remembered.focus();
              return;
            }
            const activeListItem = listEl.querySelector<HTMLElement>(LIST_ACTIVE_ITEM_SELECTOR);
            const listItems = listEl.querySelectorAll<HTMLElement>(LIST_FOCUSABLE_SELECTOR);
            const fallback = listItems[listItems.length - 1];
            (activeListItem ?? fallback)?.focus();
          }}
          sx={{
            flex: '0 0 auto',
            px: 1,
            pb: 1,
            pt: 0,
          }}
        >
          {footer}
        </Box>
      ) : null}
    </PopoverPaper>
  );
}

/**
 * Popover command surface for the choice-control family.
 * New DS primitive under `QDropdowns` — does not replace legacy `components/QMenu`.
 *
 * Aligns with MUI v6 Menu API (`@mui/material@6.5.0`):
 * - Use `slotProps.list` / `slotProps.paper` (not deprecated `MenuListProps` / `PaperProps`)
 * - `variant="menu"` for action menus; `"selectedMenu"` when selection should take focus
 * - `container` from Popover/Modal for drawer layering
 * - Scrolls only when content exceeds available viewport height (no fixed 400px cap by default)
 * - Optional `scrollArrows` (QSelect) shows click-to-jump top/bottom scroll controls
 * - Optional `header` sticky paper chrome above the scrolling MenuList
 * - Optional `footer` sticky paper chrome below the scrolling MenuList
 *
 * @see https://mui.com/material-ui/api/menu/
 * @see https://mui.com/material-ui/react-menu/
 */
export function QMenu({
  open,
  anchorEl,
  onClose,
  children,
  id,
  width,
  dense = false,
  variant = 'menu',
  maxHeight,
  scrollArrows = false,
  header = null,
  footer = null,
  autoFocusFirstItem = true,
}: QMenuProps): React.ReactElement {
  const drawerLayerContainer = useStandardDrawerLayerContainer();
  const drawerFloatingSx = getStandardDrawerFloatingSx();
  const [listEl, setListEl] = useState<HTMLElement | null>(null);
  const headerElRef = useRef<HTMLDivElement | null>(null);
  const footerElRef = useRef<HTMLDivElement | null>(null);
  const lastListFocusRef = useRef<HTMLElement | null>(null);
  const resolvedWidth = width ?? 180;
  const paperChrome = scrollArrows || isRenderableNode(header) || isRenderableNode(footer);

  // Default: available popover space (MUI MenuPaper uses calc(100% - 96px)).
  // Only scrolls when content cannot fit — do not force a fixed short viewport.
  const resolvedMaxHeight = maxHeight ?? 'calc(100% - 96px)';
  const paperLayout = useMemo(
    (): QMenuPaperLayout => ({
      width: resolvedWidth,
      maxHeight: resolvedMaxHeight,
    }),
    [resolvedMaxHeight, resolvedWidth],
  );

  const paperSlotValue = useMemo(
    (): QMenuPaperSlotContextValue => ({
      listEl,
      layout: paperLayout,
      header,
      footer,
      headerElRef,
      footerElRef,
      lastListFocusRef,
      scrollArrows,
    }),
    [listEl, paperLayout, header, footer, scrollArrows],
  );

  const paperSx: SxProps<Theme> = useMemo(
    () => (theme) => ({
      minWidth: resolvedWidth,
      width: resolvedWidth,
      maxWidth: '100%',
      maxHeight: resolvedMaxHeight,
      // theme.shape.radius.xl via sx borderRadius resolver (12px)
      borderRadius: 'xl',
      border: 'none',
      ...(paperChrome
        ? {
            overflow: 'hidden',
            py: 0,
          }
        : {
            py: theme.spacing(0.5),
          }),
    }),
    [paperChrome, resolvedMaxHeight, resolvedWidth],
  );

  const handleClose: NonNullable<MenuProps['onClose']> = useCallback(
    (event, reason) => {
      // MUI Menu closes on any Tab from the list (runtime reason "tabKeyDown"; Modal
      // typings omit it). Sticky chrome intercepts that: forward Tab → footer, Shift+Tab
      // → last header control (search). Menus without that chrome still dismiss.
      const tabEvent =
        event != null && typeof event === 'object' && 'key' in event
          ? (event as { key?: string; shiftKey?: boolean })
          : null;
      const isTab = tabEvent?.key === 'Tab';
      const isForwardTab = Boolean(isTab && !tabEvent?.shiftKey);
      const isShiftTab = Boolean(isTab && tabEvent?.shiftKey);

      const active = document.activeElement;
      const focusIsInList = active instanceof HTMLElement && Boolean(active.closest(LIST_FOCUSABLE_SELECTOR));

      if (isShiftTab && header && headerElRef.current) {
        const focusables = getChromeFocusables(headerElRef.current);
        const last = focusables[focusables.length - 1];
        if (last && focusIsInList && active instanceof HTMLElement) {
          lastListFocusRef.current = active;
          last.focus();
          return;
        }
      }
      if (isForwardTab && footer && footerElRef.current) {
        const focusable = footerElRef.current.querySelector<HTMLElement>(CHROME_FOCUSABLE_SELECTOR);
        if (focusable) {
          if (focusIsInList && active instanceof HTMLElement) {
            lastListFocusRef.current = active;
          }
          focusable.focus();
          return;
        }
      }
      // NestedQMenu parity: backdrop dismiss must not click-through to row/card
      // hosts (e.g. register row → txn drawer).
      if (reason === 'backdropClick' && event != null && typeof event === 'object' && 'stopPropagation' in event) {
        (event as { stopPropagation: () => void }).stopPropagation();
        if (
          'preventDefault' in event &&
          typeof (event as { preventDefault?: () => void }).preventDefault === 'function'
        ) {
          (event as { preventDefault: () => void }).preventDefault();
        }
      }
      onClose(event, reason);
    },
    [footer, header, onClose],
  );

  // Same node-identity guard as QSelect's trigger ref — avoid tearing down scroll
  // listeners when multi-select re-renders assign the same MenuList DOM node.
  const handleListRef = useCallback((node: HTMLElement | null): void => {
    setListEl((prev) => (prev === node ? prev : node));
  }, []);

  const listMaxHeightCss = toCssSize(resolvedMaxHeight);

  // Providers must wrap *outside* Menu — not as MenuList children. MUI MenuList
  // clones its direct children for autoFocusItem / tabIndex; a Context.Provider
  // as the sole child swallows that and breaks keyboard focus + Arrow navigation.
  return (
    <QMenuCloseProvider onClose={() => onClose()}>
      <QMenuSubLevelProvider>
        <QMenuPaperSlotContext.Provider value={paperSlotValue}>
          <Menu
            id={id}
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            container={drawerLayerContainer ?? undefined}
            sx={drawerFloatingSx}
            variant={variant}
            autoFocus={autoFocusFirstItem}
            disableAutoFocusItem={!autoFocusFirstItem}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            slots={paperChrome ? { paper: QMenuScrollPaperSlot } : undefined}
            slotProps={{
              paper: {
                sharedcomponentid: 'QMenu',
                sx: paperSx,
                elevation: 24,
                onMouseDown: (event: React.MouseEvent) => {
                  event.stopPropagation();
                },
                onClick: (event: React.MouseEvent) => {
                  event.stopPropagation();
                },
                // Inline sizing for the default (non–paper-chrome) paper path.
                ...(!paperChrome
                  ? {
                      style: {
                        width: toCssSize(resolvedWidth),
                        minWidth: toCssSize(resolvedWidth),
                        maxHeight: listMaxHeightCss,
                      },
                    }
                  : null),
              },
              list: {
                dense,
                ref: handleListRef,
                'aria-labelledby': id ? `${id}-button` : undefined,
                // Scroll lives only on the list; arrow/footer chrome is outside it in the paper slot.
                // Do not set style.width — MUI adjustStyleForScrollbar skips when width is set.
                style: paperChrome ? { flex: '1 1 0%', minHeight: 0, overflow: 'auto' } : undefined,
                sx: {
                  padding: 1,
                },
              },
            }}
          >
            {children}
          </Menu>
        </QMenuPaperSlotContext.Provider>
      </QMenuSubLevelProvider>
    </QMenuCloseProvider>
  );
}
